import { DatePicker as HDDatePicker, type DatePickerProps as Props } from '@heroui/react';
import { type DateValue, fromDate, getLocalTimeZone, today } from '@internationalized/date';
import { type Control, Controller, type FieldValues, type Path, type PathValue } from 'react-hook-form';

export const DatePicker = <T extends DateValue>(props: Props<T>) => {
  return <HDDatePicker radius='sm' {...props} />;
};

export interface FormDatePickerProps<T extends FieldValues> extends Props {
  name: Path<T>;
  control: Control<T, unknown>;
  defaultValue?: PathValue<T, Path<T>>;
  placeholder?: string;
}

export const FormDatePicker = <T extends FieldValues>({
  control,
  name,
  children,
  defaultValue,
  ...rest
}: FormDatePickerProps<T>) => {
  return (
    <Controller
      name={name}
      defaultValue={defaultValue || undefined}
      control={control}
      shouldUnregister
      render={({ field: { onChange, onBlur, value }, fieldState: { invalid, isDirty } }) => {
        const onHandleChange = (value: DateValue | null) => {
          if (!value) {
            onChange(value);
            return;
          }
          onChange(value.toDate(getLocalTimeZone()));
        };
        return (
          <DatePicker
            granularity='day'
            minValue={today(getLocalTimeZone())}
            {...rest}
            showMonthAndYearPickers
            value={value ? fromDate(new Date(value), getLocalTimeZone()) : undefined}
            isInvalid={isDirty && invalid}
            onBlur={onBlur}
            onChange={onHandleChange}
          >
            {children}
          </DatePicker>
        );
      }}
    />
  );
};
